import React from 'react';
import c from '../../constant';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Button, Box, Flex, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextFormField from '../form/TextFormField';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import useStore from '../../store';
import DropDownField from '../form/DropDownField';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const filterSchema = Yup.object().shape({
	no: Yup.number(),
	rank: Yup.string(),
	position: Yup.string(),
	office: Yup.string(),
	department: Yup.string(),
	createdDate: Yup.string(),
});

function PositionStatusFilter({
	isOpen,
	onClose,
	callback,
	no = '',
	rank = '',
	position = '',
	office = '',
	department = '',
	dateStart = null,
	dateEnd = null,
}) {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const userId = urlParams.get('userId');

	let defaultRange = '';
	if (dateStart && dateEnd) {
		const defaultFrom = JSON.parse(dateStart);

		const defaultTo = JSON.parse(dateEnd);
		defaultRange = {
			from: defaultFrom,
			to: defaultTo,
		};
	}

	const OFFICE_OPTIONS_BY_DEPARTMENT = {
		D_G_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative', value: 'office1' },
			{ label: lang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting', value: 'office2' },
			{ label: lang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource', value: 'office3' },
			{ label: lang === 'kh' ? 'ការិយាល័យទំនាក់ទំនងសាធារណៈ' : 'Public Relation', value: 'office4' },
		],

		D_L_L_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing', value: 'office5' },
			{ label: lang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration', value: 'office6' },
			{ label: lang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation', value: 'office7' },
		],

		D_S: [
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer', value: 'office8' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer', value: 'office9' },
			{
				label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'Micro Insurer',
				value: 'office10',
			},
			{ label: lang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential', value: 'office11' },
		],

		D_M_D: [
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product', value: 'office12' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product', value: 'office13' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product', value: 'office14' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision', value: 'office15' },
		],

		D_R_T_C: [
			{ label: lang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research', value: 'office16' },
			{ label: lang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training', value: 'office17' },
			{ label: lang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation', value: 'office18' },
			{ label: lang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech', value: 'office19' },
		],
	};

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								no: no ? no : '',
								rank: rank ? rank : '',
								position: position ? position : '',
								department: department ? department : '',
								office: office ? office : '',
								createdDate: defaultRange ? JSON.stringify(defaultRange) : '',
							}}
							validationSchema={filterSchema}
							onSubmit={(values) => {
								const officeList = OFFICE_OPTIONS_BY_DEPARTMENT[values?.department] || [];
								const isOfficeValidForDepartment = officeList?.some((item) => item.value === values?.office);

								let url = `?userId=${userId}`;

								if (values?.rank) {
									url += `&rank=${values?.rank}`;
								}

								if (values?.no) {
									url += `&no=${values?.no}`;
								}

								if (values?.position) {
									url += `&position=${values?.position}`;
								}

								if (values?.department) {
									url += `&department=${values?.department}`;
								}

								if (values?.department && values?.office && isOfficeValidForDepartment) {
									url += `&office=${values?.office}`;
								}

								if (values?.userId) {
									url += `&userId=${values?.userId}`;
								}

								if (values?.createdDate) {
									const tmpDate = JSON.parse(values?.createdDate);
									if (tmpDate?.from && tmpDate?.to) {
										const dateStart = JSON.stringify(tmpDate.from);
										const dateEnd = JSON.stringify(tmpDate.to);
										url += `&dateStart=${dateStart}&dateEnd=${dateEnd}`;
									}
								}

								callback(url);
								onClose();
							}}
						>
							{(props) => (
								<Box pb="4">
									<Form>
										<TextFormField {...props} label="Number" name="no" type="number" />
										<DropDownField
											{...props}
											label="Rank"
											name="rank"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ label: 'អនុមន្រ្តីកម្រិត១', value: 'LEVEL_7' },
												{ label: 'អនុមន្រ្តីកម្រិត២', value: 'LEVEL_6' },
												{ label: 'អនុមន្រ្តីកម្រិត៣', value: 'LEVEL_5' },
												{ label: 'នាយក្រមការ', value: 'LEVEL_4' },
												{ label: 'ក្រមការដើមខ្សែ', value: 'LEVEL_3' },
												{ label: 'ក្រមការ', value: 'LEVEL_2' },
												{ label: 'កម្មសិក្សា', value: 'LEVEL_1' },
											]}
										/>{' '}
										<DropDownField
											{...props}
											label="Position"
											name="position"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ value: 'DIRECTOR_GENERAL', label: 'Director General', labelKh: 'អគ្គនាយក' },
												{ value: 'DEPUTY_DIRECTOR_GENERAL', label: 'Deputy Director General', labelKh: 'អគ្គនាយករង' },
												{ value: 'HEAD_OF_DEPARTMENT', label: 'Head of Department', labelKh: 'ប្រធាននាយកដ្ឋាន' },
												{ value: 'DEPUTY_HEAD_OF_DEPARTMENT', label: 'Deputy Head of Department', labelKh: 'អនុប្រធាននាយកដ្ឋាន' },
												{ value: 'HEAD_OF_OFFICE', label: 'Head of Office', labelKh: 'ប្រធានការិយាល័យ' },
												{ value: 'DEPUTY_HEAD_OF_OFFICE', label: 'Deputy Head of Office', labelKh: 'អនុប្រធានការិយាល័យ' },
												{ value: 'STATUTE_OFFICER', label: 'Statute Officer', labelKh: 'មន្រ្តីលក្ខន្តិកៈ' },
												{ value: 'INTERNSHIP_OFFICER', label: 'Internship Officer', labelKh: 'មន្រ្តីកម្មសិក្សា' },
											]}
										/>
										<DropDownField
											{...props}
											label="Department"
											name="department"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ label: lang === 'kh' ? 'នាយកដ្ឋានកិច្ចការទូទៅ' : 'General Affairs Department', value: 'D_G_A' },
												{
													label:
														lang === 'kh'
															? 'នាយកដ្ឋានគ្រប់គ្រងអាជ្ញាបណ្ណ និងកិច្ចការគតិយុត្ត'
															: 'License and Legal Affairs Department',
													value: 'D_L_L_A',
												},
												{ label: lang === 'kh' ? 'នាយកដ្ឋានត្រួតពិនិត្យ' : 'Supervision Department', value: 'D_S' },
												{ label: lang === 'kh' ? 'នាយកដ្ឋានអភិវឌ្ឍន៍ទីផ្សារ' : 'Market Development Department', value: 'D_M_D' },
												{
													label:
														lang === 'kh'
															? 'នាយកដ្ឋានស្រាវជ្រាវ បណ្តុះបណ្តាល និងសហប្រតិបត្តិការ'
															: 'Research, Training and Cooperation Department',
													value: 'D_R_T_C',
												},
											]}
										/>
										{props.values?.department && (
											<DropDownField
												{...props}
												key={props.values?.department}
												label="Office"
												name="office"
												placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
												options={OFFICE_OPTIONS_BY_DEPARTMENT[props.values?.department] || []}
											/>
										)}
										<Flex mt="32px" justifyContent="flex-end">
											<Button
												id="login-btn"
												variant="solid"
												size="md"
												h="42px"
												w="128px"
												type="submit"
												borderRadius={c.borderRadius}
												color="#3498db"
											>
												{t('Apply')}
											</Button>
											<Button
												size="md"
												h="42px"
												ml={4}
												w="128px"
												onClick={() => {
													callback(`?userId=${userId}`);
													onClose();
												}}
												variant="ghost"
											>
												{t('Clear')}
											</Button>
										</Flex>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default PositionStatusFilter;
